import React, { useRef, useState, useEffect, useImperativeHandle, useCallback } from 'react';
import TextField from '@material-ui/core/TextField';
import './TaskManagement.css';

// Helper to check if HTML content is visually empty
export const isWysiwygEmpty = (html) => {
  if (!html) return true;
  if (typeof html !== 'string') return false;
  if (/<(img|video|audio|iframe|table|hr)\b/i.test(html)) return false;
  const clean = html
    .replace(/<[^>]+>/g, '')
    .replace(/&(nbsp|amp|quot|lt|gt);/g, ' ')
    .replace(/&#\d+;/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
  return clean.length === 0;
};

// Convert plain text or mixed bullet text into structured HTML <ul><li> with hanging indent support
export const textToWysiwygHtml = (text) => {
  if (!text || typeof text !== 'string') return '';

  let normalized = text;
  if (/<(div|p|br)\b/i.test(text) && !/<\/?(ul|ol|li)\b/i.test(text)) {
    normalized = text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<div[^>]*>/gi, '')
      .replace(/<p[^>]*>/gi, '');
  }

  const lines = normalized.split(/\r?\n/);
  let html = '';
  let inList = false;

  const sanitizeLineContent = (str) => {
    if (!str) return '';
    if (/<(b|strong|i|em|u|span|a)\b/i.test(str)) {
      return str;
    }
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const bulletMatch = rawLine.match(/^(\s*)([•◦▪\*\-]|\d+\.)\s+(.*)$/);

    if (bulletMatch) {
      const leadingSpaces = bulletMatch[1] || '';
      const bulletChar = bulletMatch[2];
      const content = (bulletMatch[3] || '').trim();

      let level = 1;
      const spaceCount = leadingSpaces.length;

      if (spaceCount >= 12) {
        level = 4;
      } else if (bulletChar === '•') {
        level = spaceCount >= 8 ? 3 : spaceCount >= 4 ? 2 : 1;
      } else if (bulletChar === '◦') {
        level = spaceCount >= 8 ? 4 : 2;
      } else if (bulletChar === '▪') {
        level = spaceCount >= 8 ? 4 : 3;
      } else {
        // '-', '*', '.'
        level = Math.min(Math.floor(spaceCount / 4) + 1, 6);
      }

      if (!inList) {
        html += '<ul>';
        inList = true;
      }

      html += `<li data-level="${level}" class="tm-bullet-l${level}">${sanitizeLineContent(content)}</li>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }

      if (!rawLine.trim()) {
        html += '<div><br></div>';
      } else {
        html += `<div>${sanitizeLineContent(rawLine)}</div>`;
      }
    }
  }

  if (inList) {
    html += '</ul>';
  }

  return html;
};

// Sanitize bullets: converts any raw bullet lines into real HTML <ul><li> with native hanging indent & levels
export const sanitizeBulletHtml = (input) => {
  if (!input || typeof input !== 'string') return '';
  if (/<\/?(ul|ol|li)\b/i.test(input)) {
    return input.replace(/<li([^>]*)>(\s*(?:[•◦▪\-\*]|\d+\.)\s*)?/gi, (match, attrs, bullet) => {
      let level = '1';
      const levelMatch = attrs.match(/data-level=["']?(\d+)["']?/i) || attrs.match(/tm-bullet-l(\d+)/i);
      if (levelMatch) {
        level = levelMatch[1];
      } else if (bullet && bullet.includes('▪')) {
        level = '3';
      } else if (bullet && bullet.includes('◦')) {
        level = '2';
      }
      return `<li data-level="${level}" class="tm-bullet-l${level}">`;
    });
  }
  if (/[•◦▪]|\n\s*[-*]\s+/.test(input) || /^\s*[-*]\s+/.test(input)) {
    return textToWysiwygHtml(input);
  }
  return input;
};

// Backward compatibility alias
export const sanitizeBulletText = (text) => sanitizeBulletHtml(text);

export const getBulletLevel = (spaces, bulletChar) => {
  const spaceCount = (spaces || '').length;
  if (bulletChar === '•') return 1;
  if (bulletChar === '◦') return 2;
  if (bulletChar === '▪') return Math.max(3, Math.floor(spaceCount / 4));
  return Math.max(1, Math.floor(spaceCount / 4));
};

export const getPrefixForLevel = (level) => {
  if (level <= 0) return '';
  if (level === 1) return '• ';
  if (level === 2) return '    ◦ ';
  const indentCount = (level - 1) * 4;
  return ' '.repeat(indentCount) + '▪ ';
};

// Custom contentEditable input component passed to Material-UI TextField via InputProps.inputComponent
const WysiwygContentInput = React.forwardRef((props, ref) => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    className,
    style,
    disabled,
    readOnly,
    rows,
    autoFocus,
    ...other
  } = props;

  const contentRef = useRef(null);
  const isTypingRef = useRef(false);

  const checkIsEmpty = (html) => isWysiwygEmpty(html);

  const [isEmpty, setIsEmpty] = useState(checkIsEmpty(value));

  // Expose focus and value to Material-UI InputBase
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (contentRef.current) {
        contentRef.current.focus();
      }
    },
    value: value || '',
    scrollWidth: contentRef.current ? contentRef.current.scrollWidth : 0,
    scrollHeight: contentRef.current ? contentRef.current.scrollHeight : 0,
  }));

  // Sync external value with editor content when not actively typing
  useEffect(() => {
    if (contentRef.current && !isTypingRef.current) {
      const currentHtml = contentRef.current.innerHTML;
      const targetVal = sanitizeBulletHtml(value || '');
      if (currentHtml !== targetVal) {
        contentRef.current.innerHTML = targetVal;
        setIsEmpty(checkIsEmpty(targetVal));
      }
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!contentRef.current) return;
    isTypingRef.current = true;
    const html = contentRef.current.innerHTML;
    const textEmpty = checkIsEmpty(html);
    setIsEmpty(textEmpty);

    if (onChange) {
      onChange({
        target: {
          value: textEmpty ? '' : html,
          name: props.name || 'description',
        },
      });
    }

    setTimeout(() => {
      isTypingRef.current = false;
    }, 0);
  }, [onChange, props.name]);

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const text = clipboardData ? clipboardData.getData('text/plain') : '';
    if (text) {
      const html = textToWysiwygHtml(text);
      document.execCommand('insertHTML', false, html);
      handleInput();
    }
  };

  const getClosestLi = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !contentRef.current) return null;
    let node = sel.getRangeAt(0).startContainer;
    while (node && node !== contentRef.current) {
      if (node.nodeName === 'LI') return node;
      node = node.parentNode;
    }
    return null;
  };

  const handleKeyDown = (e) => {
    // Rich Text Formatting Shortcuts: Ctrl+B, Ctrl+I, Ctrl+U
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === 'b') {
        e.preventDefault();
        document.execCommand('bold', false, null);
        handleInput();
        return;
      }
      if (key === 'i') {
        e.preventDefault();
        document.execCommand('italic', false, null);
        handleInput();
        return;
      }
      if (key === 'u') {
        e.preventDefault();
        document.execCommand('underline', false, null);
        handleInput();
        return;
      }
    }

    // 1. Space key: convert "- ", "* ", ". ", or "• " at line start into real HTML list with hanging indent & front space
    if (e.key === ' ' || e.code === 'Space') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount && contentRef.current) {
        const range = sel.getRangeAt(0);
        if (range.collapsed && typeof sel.modify === 'function') {
          const cloneRange = range.cloneRange();
          sel.removeAllRanges();
          sel.addRange(cloneRange);
          sel.modify('extend', 'backward', 'lineboundary');
          const textBefore = sel.toString();

          const match = textBefore.match(/^(\s*)([-*•◦▪]|\.)\s*$/);
          if (match && ['-', '*', '.', '•', '◦', '▪'].includes(match[2])) {
            e.preventDefault();
            const spaceCount = match[1].length;
            let level = 1;
            if (spaceCount >= 12) {
              level = 4;
            } else if (match[2] === '▪' || spaceCount >= 8) {
              level = 3;
            } else if (match[2] === '◦' || spaceCount >= 4) {
              level = 2;
            }

            document.execCommand('delete');
            const currentLi = getClosestLi();
            if (!currentLi) {
              document.execCommand('insertUnorderedList', false, null);
            }
            const li = getClosestLi();
            if (li) {
              li.setAttribute('data-level', String(level));
              li.className = 'tm-bullet-l' + level;
            }
            handleInput();
            return;
          } else {
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      }
      return;
    }

    // 2. Tab key: Indent to sub-point (Level 2: ◦, Level 3: ▪) or outdent
    if (e.key === 'Tab') {
      e.preventDefault();
      const li = getClosestLi();
      if (li) {
        const currentLevel = parseInt(li.getAttribute('data-level') || '1', 10);
        if (e.shiftKey) {
          // Outdent
          if (currentLevel > 1) {
            const newLevel = currentLevel - 1;
            li.setAttribute('data-level', String(newLevel));
            li.className = 'tm-bullet-l' + newLevel;
            handleInput();
            return;
          } else {
            // Level 1 outdents to regular paragraph
            document.execCommand('outdent', false, null);
            handleInput();
            return;
          }
        } else {
          // Indent to sub-point! Support up to level 6
          const newLevel = Math.min(currentLevel + 1, 6);
          li.setAttribute('data-level', String(newLevel));
          li.className = 'tm-bullet-l' + newLevel;
          handleInput();
          return;
        }
      }

      // Check if user typed "-" or "*" or "." right before cursor
      const sel = window.getSelection();
      if (sel && sel.rangeCount && !e.shiftKey && typeof sel.modify === 'function') {
        const range = sel.getRangeAt(0);
        const cloneBefore = range.cloneRange();
        sel.removeAllRanges();
        sel.addRange(cloneBefore);
        sel.modify('extend', 'backward', 'lineboundary');
        const textBefore = sel.toString();

        const match = textBefore.match(/^(\s*)([-*•◦▪]|\.)\s*$/);
        if (match && ['-', '*', '.', '•', '◦', '▪'].includes(match[2])) {
          document.execCommand('delete');
          document.execCommand('insertUnorderedList', false, null);
          const newLi = getClosestLi();
          if (newLi) {
            newLi.setAttribute('data-level', '2');
            newLi.className = 'tm-bullet-l2';
          }
          handleInput();
          return;
        } else {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }

      if (!e.shiftKey) {
        document.execCommand('insertText', false, '    ');
        handleInput();
        return;
      }
      return;
    }

    // 3. Enter key: continue list at the SAME level or cleanly exit on empty bullet
    if (e.key === 'Enter') {
      const li = getClosestLi();
      if (li) {
        const textContent = (li.textContent || '').replace(/[\s\u200B-\u200D\uFEFF]/g, '');
        const currentLevel = parseInt(li.getAttribute('data-level') || '1', 10);

        if (textContent.length === 0) {
          // Empty bullet line
          e.preventDefault();
          if (currentLevel > 1) {
            // Sub-point outdents to parent level
            const newLevel = currentLevel - 1;
            li.setAttribute('data-level', String(newLevel));
            li.className = 'tm-bullet-l' + newLevel;
          } else {
            // Level 1 exits the list to normal line
            document.execCommand('outdent', false, null);
          }
          handleInput();
          return;
        }

        // Line has text: split item at cursor and create next <li> at the SAME level!
        e.preventDefault();
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
          const range = sel.getRangeAt(0);

          // Extract everything from cursor to end of current li
          const afterRange = range.cloneRange();
          afterRange.setEndAfter(li.lastChild || li);
          const afterContent = afterRange.extractContents();

          // Create new li with the extracted content
          const newLi = document.createElement('li');
          newLi.setAttribute('data-level', String(currentLevel));
          newLi.className = 'tm-bullet-l' + currentLevel;

          const hasText = afterContent.textContent && afterContent.textContent.replace(/[\s\u200B-\u200D\uFEFF]/g, '').length > 0;
          const hasMedia = afterContent.querySelector && Boolean(afterContent.querySelector('img, br, iframe, video'));

          if (!hasText && !hasMedia) {
            newLi.innerHTML = '<br>';
          } else {
            // Trim leading whitespace on the new line if split right after a space
            if (afterContent.firstChild && afterContent.firstChild.nodeType === Node.TEXT_NODE) {
              afterContent.firstChild.textContent = afterContent.firstChild.textContent.replace(/^\s+/, '');
            }
            newLi.appendChild(afterContent);
          }

          // If current li became empty (e.g. Enter pressed at start of line), keep <br>
          const liHasText = li.textContent && li.textContent.replace(/[\s\u200B-\u200D\uFEFF]/g, '').length > 0;
          const liHasMedia = li.querySelector && Boolean(li.querySelector('img, br, iframe, video'));
          if (!liHasText && !liHasMedia) {
            li.innerHTML = '<br>';
          }

          // Insert newLi immediately after current li
          if (li.nextSibling) {
            li.parentNode.insertBefore(newLi, li.nextSibling);
          } else {
            li.parentNode.appendChild(newLi);
          }

          // Place caret at start of newLi
          const newRange = document.createRange();
          if (newLi.firstChild) {
            newRange.setStart(newLi.firstChild, 0);
          } else {
            newRange.setStart(newLi, 0);
          }
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
          handleInput();

          // Automatically scroll newly created item into view so user doesn't have to scroll manually
          setTimeout(() => {
            if (newLi) {
              if (typeof newLi.scrollIntoView === 'function') {
                newLi.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
              }
              if (contentRef.current) {
                const parent = contentRef.current;
                const liBottom = newLi.offsetTop + (newLi.offsetHeight || 24);
                if (liBottom > parent.scrollTop + parent.clientHeight - 10) {
                  parent.scrollTop = liBottom - parent.clientHeight + 28;
                }
              }
            }
          }, 0);

          return;
        }
      } else {
        // Outside of li: ensure caret scrolls into view after native Enter
        setTimeout(() => {
          if (contentRef.current) {
            const parent = contentRef.current;
            const sel = window.getSelection();
            if (sel && sel.rangeCount) {
              const range = sel.getRangeAt(0);
              const rect = range.getBoundingClientRect();
              const parentRect = parent.getBoundingClientRect();
              if (rect.bottom > parentRect.bottom - 10) {
                parent.scrollTop += (rect.bottom - parentRect.bottom) + 28;
              }
            }
          }
        }, 10);
      }
    }

    // 4. Backspace key: remove bullet cleanly on empty bullet line
    if (e.key === 'Backspace') {
      const li = getClosestLi();
      if (li) {
        const textContent = (li.textContent || '').replace(/[\s\u200B-\u200D\uFEFF]/g, '');
        const currentLevel = parseInt(li.getAttribute('data-level') || '1', 10);
        const sel = window.getSelection();
        const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;

        if (range && range.collapsed && (textContent.length === 0 || range.startOffset === 0)) {
          e.preventDefault();
          if (currentLevel > 1) {
            const newLevel = currentLevel - 1;
            li.setAttribute('data-level', String(newLevel));
            li.className = 'tm-bullet-l' + newLevel;
          } else {
            if (textContent.length === 0) {
              const parent = li.parentNode;
              li.remove();
              if (parent && !parent.hasChildNodes()) {
                parent.remove();
              }
            } else {
              document.execCommand('outdent', false, null);
            }
          }
          handleInput();
          return;
        }
      }
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <div
      ref={contentRef}
      id={props.id}
      className={`tm-wysiwyg-content ${className || ''}`}
      contentEditable={!disabled && !readOnly}
      suppressContentEditableWarning
      data-empty={isEmpty}
      data-placeholder={placeholder || ''}
      onFocus={(e) => {
        if (onFocus) onFocus(e);
      }}
      onBlur={(e) => {
        if (contentRef.current) {
          const raw = contentRef.current.innerHTML;
          const sanitized = sanitizeBulletHtml(raw);
          if (sanitized !== raw) {
            contentRef.current.innerHTML = sanitized;
          }
        }
        handleInput();
        if (onBlur) onBlur(e);
      }}
      onPaste={handlePaste}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      style={{
        position: 'relative',
        minHeight: 110,
        maxHeight: 400,
        outline: 'none',
        width: '100%',
        textAlign: 'left',
        direction: 'ltr',
        cursor: 'text',
        ...style,
      }}
      {...other}
    />
  );
});

// Full Material-UI TextField with WysiwygContentInput as its inputComponent
const WysiwygDescriptionEditor = ({
  value = '',
  onChange,
  onFocus,
  onBlur,
  label = 'Description',
  placeholder = 'Enter description...',
  error = false,
  helperText = '',
  id = 'description',
  name = 'description',
  rows = 4,
  minHeight,
  className = '',
  InputLabelProps,
  InputProps,
  inputProps,
  ...otherProps
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const hasContent = !isWysiwygEmpty(value);
  const isShrunk = (InputLabelProps && typeof InputLabelProps.shrink !== 'undefined')
    ? InputLabelProps.shrink
    : Boolean(isFocused || hasContent);

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  return (
    <TextField
      fullWidth
      multiline
      rows={rows}
      variant="outlined"
      id={id}
      name={name}
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onChange={(e) => {
        if (onChange) {
          onChange(e.target.value);
        }
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      error={Boolean(error)}
      helperText={helperText}
      className={`tm-wysiwyg-mui-field ${error ? 'input-error' : ''} ${className}`}
      InputLabelProps={{
        shrink: isShrunk,
        ...InputLabelProps,
      }}
      InputProps={{
        inputComponent: WysiwygContentInput,
        onFocus: handleFocus,
        onBlur: handleBlur,
        style: minHeight ? { minHeight, ...(InputProps && InputProps.style) } : (InputProps && InputProps.style),
        ...InputProps,
      }}
      inputProps={{
        placeholder: placeholder,
        id: id,
        name: name,
        onFocus: handleFocus,
        onBlur: handleBlur,
        style: minHeight ? { minHeight: minHeight - 20, ...(inputProps && inputProps.style) } : (inputProps && inputProps.style),
        ...inputProps,
      }}
      {...otherProps}
    />
  );
};

export default WysiwygDescriptionEditor;
